import { cityLine } from "../../utils/shortAddress";

// Words and rules for the no-delivery view (PickupHome), shared with the
// customer app's Home/Pickup so the two say the same thing.

/**
 * Nothing delivers here: the list came back empty, or holds only food trucks
 * that can't come to this address (nearByRestaurants keeps every nearby truck;
 * fixed restaurants are only listed when their zones cover the address).
 */
export const nothingDelivers = (restaurants) =>
  (restaurants || []).every((r) => r.isMobile === true && r.deliveryAvailable !== true);

/** "15 Wright St", from "15 Wright St, Stoneham, MA 02180, USA". */
export const streetOf = (location) => String(location?.deliveryAddress || "").split(",")[0].trim() || "this address";

/** "15 miles", "1 mile", "7.5 miles". */
export function radiusText(miles) {
  const n = Number(miles);
  if (miles == null || !Number.isFinite(n)) return "15 miles";
  const shown = Number.isInteger(n) ? String(n) : n.toFixed(1);
  return `${shown} ${n === 1 ? "mile" : "miles"}`;
}

/** The town alone: "Stoneham". */
export const townOf = (address) => cityLine(address || "").split(",")[0].trim();

/** "6.7 mi" from a distance in km. */
export const milesText = (km) => (km == null || !Number.isFinite(Number(km)) ? null : `${(Number(km) * 0.621371).toFixed(1)} mi`);

// ~2 min per straight-line mile plus a couple to get going: roads are longer
// than the crow flies and town speeds are ~30 mph. Same as the app.
export function estimateDriveMinutes(km) {
  if (km == null || !Number.isFinite(Number(km))) return null;
  return Math.max(2, Math.round(Number(km) * 0.621371 * 2 + 2));
}

export const readyMinutesOf = (r) => Number(r.defaultPrepTime) || Number(r.preparationTime) || 15;

/** "American & Pizza · Lynn": two cuisines and the town from the address. */
export function cuisineLine(r) {
  const cuisines = (r.cuisines || []).filter(Boolean).slice(0, 2).join(" & ");
  return [cuisines, r.city || townOf(r.address)].filter(Boolean).join(" · ");
}

/** A saved address as the page's location (same shape as Profile's address list). */
export function addressToLocation(a) {
  const c = a?.location?.coordinates || [];
  return {
    _id: a?._id,
    label: a?.label,
    latitude: Number(c[1]),
    longitude: Number(c[0]),
    deliveryAddress: a?.deliveryAddress,
    details: a?.details,
    apartment: a?.apartment,
    buildingName: a?.buildingName,
    entryCode: a?.entryCode,
    dropoffOption: a?.dropoffOption,
    instructions: a?.instructions,
  };
}

/** A saved address's point, or null when it has no usable coordinates. */
export function pointOf(a) {
  const c = a?.location?.coordinates;
  const lat = Number(c?.[1]);
  const lng = Number(c?.[0]);
  return c && Number.isFinite(lat) && Number.isFinite(lng) && (lat || lng) ? { latitude: lat, longitude: lng } : null;
}
